/**
 * Map basemap configuration — one place, not two.
 *
 * Backlog §15. The tile URL and attribution were hardcoded identically in
 * LiveMap.jsx and LiveTracking.jsx. Change one, forget the other, and the two
 * maps diverge — the same duplication shape as Unit F's three copies of the
 * admin tuple.
 *
 * Set REACT_APP_MAP_TILE_URL to the FULL tile URL your provider gives you,
 * including any api key parameter. Paste it exactly as they give it; do not
 * try to assemble it by hand.
 *
 * ⚠ Anything in a REACT_APP_* variable is PUBLIC. Create React App inlines it
 * into the JavaScript bundle at build time, so every visitor can read it in
 * devtools. That is unavoidable for a browser map key and is fine *provided
 * the key is restricted by domain* in the provider's dashboard. A key that
 * works from any origin is one anyone can lift and spend your quota with.
 * Never put a secret — anything that is not safe to publish — in one of these.
 *
 * The attribution is a licence condition, not decoration. If the provider
 * changes, this must change with it.
 */

// The unauthenticated CARTO CDN. Still the default so nothing breaks without
// configuration — but as of 2026-09-18 it serves tiles watermarked
// "API KEY REQUIRED", with HTTP 200 and a valid image/png, so nothing errors
// and nothing logs. The only symptom is visual.
const DEFAULT_TILE_URL =
  "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png";
const DEFAULT_ATTRIBUTION = "&copy; OpenStreetMap &copy; CARTO";

export const MAP_TILE_URL =
  process.env.REACT_APP_MAP_TILE_URL || DEFAULT_TILE_URL;

export const MAP_ATTRIBUTION =
  process.env.REACT_APP_MAP_ATTRIBUTION || DEFAULT_ATTRIBUTION;

/**
 * The tile URL for the current theme.
 *
 * CARTO publishes the same map as dark_all and light_all, on the same key, so
 * the light theme swaps the style name and nothing else. A provider URL with
 * no dark_all in it is returned unchanged in both themes.
 */
export function tileUrlFor(theme) {
  return theme === "light"
    ? MAP_TILE_URL.replace("/dark_all/", "/light_all/")
    : MAP_TILE_URL;
}

// Leaflet only rotates {s} if it is told which subdomains exist. Providers that
// do not use {s} ignore this.
export const MAP_SUBDOMAINS =
  process.env.REACT_APP_MAP_SUBDOMAINS || "abcd";

/** True when still on the unconfigured default — used to warn once at startup. */
export const MAP_IS_UNCONFIGURED = !process.env.REACT_APP_MAP_TILE_URL;

if (MAP_IS_UNCONFIGURED && typeof console !== "undefined") {
  // Cheap, once, and the alternative is a watermark nobody can explain.
  console.warn(
    "[map] REACT_APP_MAP_TILE_URL is not set — using the unauthenticated CARTO " +
    "CDN, which serves tiles watermarked 'API KEY REQUIRED'. See backlog §15."
  );
}
